import React, { useState } from 'react';
import { Crown, Shield, Search, MoreHorizontal, MessageCircle, UserPlus, Gamepad2, Music, Code, Radio, Zap, TrendingUp, Calendar, Gift } from 'lucide-react';
import './MembersSidebar.css';

const serverMembers = {
  0: {
    online: [
      { id: 1, name: 'Alex', status: 'online', role: 'owner', isCommander: true, activity: 'Playing Minecraft' },
      { id: 2, name: 'Jordan', status: 'online', role: 'admin', isCommander: true, activity: null },
      { id: 3, name: 'Sam', status: 'idle', role: 'commander', isCommander: true, activity: 'Listening to Spotify' },
      { id: 4, name: 'Taylor', status: 'dnd', role: 'member', isCommander: false, activity: null },
      { id: 5, name: 'Riley', status: 'online', role: 'member', isCommander: false, activity: 'Streaming' },
    ],
    offline: [
      { id: 6, name: 'Morgan', status: 'offline', role: 'member', isCommander: false, activity: null },
      { id: 7, name: 'Casey', status: 'offline', role: 'commander', isCommander: true, activity: null },
      { id: 8, name: 'Quinn', status: 'offline', role: 'moderator', isCommander: false, activity: null },
    ]
  },
  1: {
    online: [
      { id: 1, name: 'Player1', status: 'online', role: 'owner', isCommander: true, activity: 'Playing Valorant' },
      { id: 2, name: 'Player2', status: 'online', role: 'commander', isCommander: true, activity: 'In Queue' },
      { id: 3, name: 'Player3', status: 'dnd', role: 'member', isCommander: false, activity: null },
    ],
    offline: [
      { id: 4, name: 'Player4', status: 'offline', role: 'member', isCommander: false, activity: null },
    ]
  },
  2: {
    online: [
      { id: 1, name: 'Dev1', status: 'online', role: 'owner', isCommander: true, activity: 'In VS Code' },
      { id: 2, name: 'Dev2', status: 'idle', role: 'admin', isCommander: true, activity: null },
    ],
    offline: [
      { id: 3, name: 'Dev3', status: 'offline', role: 'member', isCommander: false, activity: null },
      { id: 4, name: 'Dev4', status: 'offline', role: 'commander', isCommander: true, activity: null },
    ]
  },
  3: {
    online: [
      { id: 1, name: 'DJ_Mike', status: 'online', role: 'owner', isCommander: true, activity: 'Mixing' },
      { id: 2, name: 'Listener1', status: 'online', role: 'member', isCommander: false, activity: null },
    ],
    offline: []
  },
  4: {
    online: [
      { id: 1, name: 'Artist', status: 'online', role: 'owner', isCommander: true, activity: 'Drawing' },
    ],
    offline: [
      { id: 2, name: 'Curator', status: 'offline', role: 'admin', isCommander: true, activity: null },
    ]
  }
};

export { serverMembers };

function MembersSidebar({ serverId }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredMember, setHoveredMember] = useState(null);
  const members = serverMembers[serverId] || serverMembers[0];

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#00d4aa';
      case 'idle': return '#ffb800';
      case 'dnd': return '#ff4757';
      default: return '#556677';
    }
  };

  const getRoleIcon = (role, isCommander) => {
    if (role === 'owner') return <Crown size={12} className="role-icon owner" />;
    if (role === 'admin') return <Shield size={12} className="role-icon admin" />;
    if (role === 'moderator') return <Shield size={12} className="role-icon admin" />;
    if (role === 'commander' || isCommander) return <Radio size={12} className="role-icon commander" />;
    return null;
  };

  const getRoleColor = (role, isCommander) => {
    switch (role) {
      case 'owner': return '#ffb800';
      case 'admin': return '#a855f7';
      case 'moderator': return '#00a8ff';
      case 'commander': return '#ff6b6b';
      default: return isCommander ? '#ff6b6b' : 'var(--text-primary)';
    }
  };

  const getActivityIcon = (activity) => {
    if (!activity) return null;
    if (activity.toLowerCase().includes('playing') || activity.toLowerCase().includes('game')) return <Gamepad2 size={12} />;
    if (activity.toLowerCase().includes('music') || activity.toLowerCase().includes('spotify') || activity.toLowerCase().includes('listening')) return <Music size={12} />;
    if (activity.toLowerCase().includes('code') || activity.toLowerCase().includes('vs code')) return <Code size={12} />;
    return null;
  };

  const filteredOnline = members.online.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredOffline = members.offline.filter(m => 
    m.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="members-sidebar">
      <div className="members-header">
        <h3>Members</h3>
        <span className="member-count">{members.online.length + members.offline.length}</span>
      </div>

      <div className="members-search">
        <Search size={14} />
        <input 
          type="text" 
          placeholder="Search members..." 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="members-list">
        <div className="members-section">
          <div className="section-header">
            <span className="section-dot online" />
            Online — {filteredOnline.length}
          </div>
          {filteredOnline.map((member) => (
            <div 
              key={member.id} 
              className={`member-item ${member.isCommander ? 'is-commander' : ''}`}
              onMouseEnter={() => setHoveredMember(member.id)}
              onMouseLeave={() => setHoveredMember(null)}
            >
              <div className="member-avatar" style={{ background: `linear-gradient(135deg, ${getRoleColor(member.role, member.isCommander)}40, ${getRoleColor(member.role, member.isCommander)}20)` }}>
                <span style={{ color: getRoleColor(member.role, member.isCommander) }}>{member.name[0]}</span>
                <div 
                  className="member-status" 
                  style={{ background: getStatusColor(member.status), boxShadow: `0 0 6px ${getStatusColor(member.status)}` }}
                />
                {member.isCommander && <div className="commander-badge"><Radio size={8} /></div>}
              </div>
              <div className="member-info">
                <div className="member-name" style={{ color: getRoleColor(member.role, member.isCommander) }}>
                  {member.name}
                  {getRoleIcon(member.role, member.isCommander)}
                </div>
                {member.activity && (
                  <div className="member-activity">
                    {getActivityIcon(member.activity)}
                    <span>{member.activity}</span>
                  </div>
                )}
              </div>
              {hoveredMember === member.id && (
                <div className="member-actions">
                  <button title="Message"><MessageCircle size={14} /></button>
                  <button title="More"><MoreHorizontal size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredOffline.length > 0 && (
          <div className="members-section">
            <div className="section-header">
              <span className="section-dot offline" />
              Offline — {filteredOffline.length}
            </div>
            {filteredOffline.map((member) => (
              <div 
                key={member.id} 
                className="member-item offline"
                onMouseEnter={() => setHoveredMember(`offline-${member.id}`)}
                onMouseLeave={() => setHoveredMember(null)}
              >
                <div className="member-avatar">
                  <span>{member.name[0]}</span>
                  <div 
                    className="member-status" 
                    style={{ background: getStatusColor(member.status) }}
                  />
                </div>
                <div className="member-info">
                  <div className="member-name">
                    {member.name}
                    {getRoleIcon(member.role)}
                  </div>
                </div>
                {hoveredMember === `offline-${member.id}` && (
                  <div className="member-actions">
                    <button title="Add Friend"><UserPlus size={14} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="members-footer">
        <div className="quick-actions">
          <div className="quick-action-title">Quick Actions</div>
          <div className="quick-action-grid">
            <button className="quick-action-btn" title="Server Boost">
              <Zap size={16} />
              <span>Boost</span>
            </button>
            <button className="quick-action-btn" title="Server Stats">
              <TrendingUp size={16} />
              <span>Stats</span>
            </button>
            <button className="quick-action-btn" title="Events">
              <Calendar size={16} />
              <span>Events</span>
            </button>
            <button className="quick-action-btn" title="Rewards">
              <Gift size={16} />
              <span>Rewards</span>
            </button>
          </div>
        </div>
        <div className="server-activity">
          <div className="activity-title">Recent Activity</div>
          <div className="activity-item">
            <div className="activity-dot pulse"></div>
            <span>3 members joined voice</span>
          </div>
          <div className="activity-item">
            <div className="activity-dot"></div>
            <span>12 new messages today</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MembersSidebar;
